import React from "react";
import { Modal, View, StyleSheet, Text, Pressable } from "react-native";
import { XIcon } from "@phosphor-icons/react";
import AxioButton from "../Button/AxioButton";
import { colors, radii, spacing } from "../../../styles/design-system";
import { getModalAnimationType } from "../../../utils/getModalAnimationType";
import { useFocusTrap } from "../../../hooks/useFocusTrap";

// Shared backdrop wrapper
interface ModalShellProps {
  visible: boolean;
  onRequestClose: () => void;
  children: React.ReactNode;
  ariaLabel?: string;
}

const AxioModalShell: React.FC<ModalShellProps> = ({ visible, onRequestClose, children, ariaLabel }) => {
  const { trapRef } = useFocusTrap({ visible, onEscape: onRequestClose });

  return (
    <Modal
      role="dialog"
      aria-modal={true}
      aria-label={ariaLabel ?? "Modal"}
      animationType={getModalAnimationType("fade")}
      transparent
      visible={visible}
      onRequestClose={onRequestClose}>
      <View style={styles.backdrop}>
        <View ref={trapRef} style={styles.trapContainer}>
          {children}
        </View>
      </View>
    </Modal>
  );
};

// 1) General modal – header with close, body, no footer buttons
export interface AxioGeneralModalProps {
  visible: boolean;
  onRequestClose: () => void;
  title?: string;
  subtitle?: string;
  iconLeft?: React.ReactNode;
  children?: React.ReactNode;
  ariaLabel?: string;
}

export const AxioGeneralModal: React.FC<AxioGeneralModalProps> = ({
  visible,
  onRequestClose,
  title,
  subtitle,
  iconLeft,
  children,
  ariaLabel,
}) => (
  <AxioModalShell visible={visible} onRequestClose={onRequestClose} ariaLabel={ariaLabel}>
    <View style={styles.modal}>
      <View style={styles.generalHeaderSection}>
        <View style={styles.headerRow}>
          <View style={styles.headerTitleRow}>
            {iconLeft ? <View style={styles.headerIcon}>{iconLeft}</View> : null}
            <Text style={styles.title} role="heading" aria-level={2} accessibilityRole="header">
              {title}
            </Text>
          </View>
          <Pressable
            accessibilityRole="button"
            accessibilityLabel="Close modal"
            onPress={onRequestClose}
            style={styles.closeButton}>
            <XIcon size={18} color={colors.grey[500]} />
          </Pressable>
        </View>
        {subtitle ? <Text style={styles.subtitle}>{subtitle}</Text> : null}
      </View>
      <View style={styles.bodySection}>{children}</View>
    </View>
  </AxioModalShell>
);

// 2) Form modal – header with close, body, footer with primary/secondary buttons
export interface AxioFormModalProps {
  visible: boolean;
  onRequestClose: () => void;
  title?: string;
  subtitle?: string;
  iconLeft?: React.ReactNode;
  children?: React.ReactNode;
  primaryActionLabel: string;
  onPrimaryAction: () => void;
  secondaryActionLabel?: string;
  onSecondaryAction?: () => void;
  ariaLabel?: string;
}

export const AxioFormModal: React.FC<AxioFormModalProps> = ({
  visible,
  onRequestClose,
  title,
  subtitle,
  iconLeft,
  children,
  primaryActionLabel,
  onPrimaryAction,
  secondaryActionLabel,
  onSecondaryAction,
  ariaLabel,
}) => (
  <AxioModalShell visible={visible} onRequestClose={onRequestClose} ariaLabel={ariaLabel}>
    <View style={styles.modal}>
      <View style={styles.formHeaderSection}>
        <View style={styles.headerRow}>
          <View style={styles.headerTitleRow}>
            {iconLeft ? <View style={styles.headerIcon}>{iconLeft}</View> : null}
            <Text style={styles.title} role="heading" aria-level={2} accessibilityRole="header">
              {title}
            </Text>
          </View>
          <Pressable
            accessibilityRole="button"
            accessibilityLabel="Close modal"
            onPress={onRequestClose}
            style={styles.closeButton}>
            <XIcon size={18} color={colors.grey[500]} />
          </Pressable>
        </View>
        {subtitle ? <Text style={styles.subtitle}>{subtitle}</Text> : null}
      </View>
      <View style={styles.bodySection}>{children}</View>
      <View style={styles.footerRow}>
        {secondaryActionLabel ? (
          <View style={styles.footerButtonWrapper}>
            <AxioButton label={secondaryActionLabel} variant="ghost" size="md" onPress={onSecondaryAction} />
          </View>
        ) : null}
        <View style={styles.footerButtonWrapper}>
          <AxioButton label={primaryActionLabel} variant="primary" size="md" onPress={onPrimaryAction} />
        </View>
      </View>
    </View>
  </AxioModalShell>
);

// 3) Action modal – centered title/content, no top-right close, buttons centered
export interface AxioActionModalProps {
  visible: boolean;
  onRequestClose: () => void;
  title: string;
  children?: React.ReactNode;
  primaryActionLabel: string;
  onPrimaryAction: () => void;
  secondaryActionLabel?: string;
  onSecondaryAction?: () => void;
  ariaLabel?: string;
}

export const AxioActionModal: React.FC<AxioActionModalProps> = ({
  visible,
  onRequestClose,
  title,
  children,
  primaryActionLabel,
  onPrimaryAction,
  secondaryActionLabel,
  onSecondaryAction,
  ariaLabel,
}) => (
  <AxioModalShell visible={visible} onRequestClose={onRequestClose} ariaLabel={ariaLabel}>
    <View style={styles.actionModal}>
      <Text style={styles.actionTitle} role="heading" aria-level={2} accessibilityRole="header">
        {title}
      </Text>
      {children ? <View style={styles.actionBody}>{children}</View> : null}
      <View style={styles.actionFooter}>
        {secondaryActionLabel ? (
          <View style={styles.actionButtonWrapper}>
            <AxioButton label={secondaryActionLabel} variant="secondary" size="md" onPress={onSecondaryAction} />
          </View>
        ) : null}
        <View style={styles.actionButtonWrapper}>
          <AxioButton label={primaryActionLabel} variant="destructive" size="md" onPress={onPrimaryAction} />
        </View>
      </View>
    </View>
  </AxioModalShell>
);

const styles = StyleSheet.create({
  backdrop: {
    flex: 1,
    backgroundColor: "rgba(15, 23, 42, 0.45)",
    alignItems: "center",
    justifyContent: "center",
    padding: spacing.lg,
  },
  trapContainer: {
    width: "100%",
    alignItems: "center",
    justifyContent: "center",
  },
  // Shared modal shell (general + form)
  modal: {
    width: "100%",
    maxWidth: 520,
    borderRadius: radii.lg,
    backgroundColor: colors.white,
    overflow: "hidden",
  },
  generalHeaderSection: {
    backgroundColor: colors.white,
    paddingHorizontal: spacing.lg,
    paddingTop: spacing.lg,
    paddingBottom: spacing.sm,
  },
  formHeaderSection: {
    backgroundColor: colors.grey[25],
    paddingHorizontal: spacing.lg,
    paddingTop: spacing.lg,
    paddingBottom: spacing.md,
    borderBottomWidth: 1,
    borderBottomColor: colors.grey[200],
  },
  headerRow: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
  },
  headerTitleRow: {
    flexDirection: "row",
    alignItems: "center",
  },
  headerIcon: {
    marginRight: spacing.xs,
  },
  title: {
    fontSize: 18,
    fontWeight: "600",
    color: colors.grey[900],
  },
  subtitle: {
    marginTop: spacing.xs,
    fontSize: 14,
    color: colors.grey[500],
  },
  closeButton: {
    width: 32,
    height: 32,
    borderRadius: 999,
    alignItems: "center",
    justifyContent: "center",
  },
  bodySection: {
    paddingHorizontal: spacing.lg,
    paddingVertical: spacing.lg,
  },
  footerRow: {
    flexDirection: "row",
    justifyContent: "flex-end",
    paddingHorizontal: spacing.lg,
    paddingBottom: spacing.lg,
  },
  footerButtonWrapper: {
    marginLeft: spacing.xs,
  },
  // Action modal specific
  actionModal: {
    width: "100%",
    maxWidth: 480,
    borderRadius: radii.lg,
    backgroundColor: colors.white,
    paddingHorizontal: spacing.lg,
    paddingTop: spacing.xl,
    paddingBottom: spacing.lg,
    alignItems: "center",
  },
  actionTitle: {
    fontSize: 20,
    fontWeight: "700",
    color: colors.grey[900],
    textAlign: "center",
    marginBottom: spacing.sm,
  },
  actionBody: {
    marginBottom: spacing.lg,
  },
  actionFooter: {
    flexDirection: "row",
    justifyContent: "center",
  },
  actionButtonWrapper: {
    marginHorizontal: spacing.xs,
  },
});

// Default export keeps backward compatibility as a general modal
export default AxioGeneralModal;
